// MOJIBAKE_FINAL_FIX_APPLIED
// =====================================================================
// /admin/conges - Concedii admin (Faza 3.6 Round 1)
// =====================================================================
// 3 taburi:
//   1. À valider — listă demandes PENDING + buttons Approve/Reject
//   2. Encodage direct — form admin direct create (status APPROVED auto)
//   3. Cotes annuelles — manage quotas per user/year (form 7 fields)
// =====================================================================

import { useState, useEffect } from 'react';
import { leaves as leavesApi, leaveBalances as balanceApi, users as usersApi, overtime as overtimeApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { marqueurInactif } from '../../lib/tri-personnes.js';

const LEAVE_TYPE_LABELS = {
  VA: 'Vacances annuelles',
  VAN: 'Vacances année N+1',
  VAEN1: 'Vacances reportées (N-1)',
  AA: 'Absences autorisées',
  REC: 'Récupération',
  MAT: 'Maternité',
  PAT: 'Paternité',
  MAL: 'Maladie',
  'VA-0.5': 'Vacances (1/2j)',
  CC: 'Congé de circonstance',
  JFR: 'Férié de remplacement',
};

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateFR(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #CBD5E1',
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  background: 'white',
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 500,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle = { padding: '8px 12px', fontSize: 13 };

export default function AdminConges() {
  const { can } = useAuth();
  const [activeTab, setActiveTab] = useState('valider');

  const canApprove = can('leaves.approve.all');
  const canRead = can('leaves.read.all');

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <h1 style={{ marginBottom: 16, fontSize: 24 }}>Gestion des congés</h1>

      <div style={{
        display: 'flex', gap: 4, borderBottom: '2px solid #E2E8F0', marginBottom: 24,
      }}>
        <TabButton active={activeTab === 'valider'} onClick={() => setActiveTab('valider')}>À valider</TabButton>
        <TabButton active={activeTab === 'direct'} onClick={() => setActiveTab('direct')}>Encodage direct</TabButton>
        <TabButton active={activeTab === 'cotes'} onClick={() => setActiveTab('cotes')}>Quotas annuels</TabButton>
      </div>

      {activeTab === 'valider' && (canApprove ? <TabValider /> : <NoPermission perm="leaves.approve.all" />)}
      {activeTab === 'direct' && (canApprove ? <TabDirect /> : <NoPermission perm="leaves.approve.all" />)}
      {activeTab === 'cotes' && (canApprove ? <TabCotes /> : <NoPermission perm="leaves.approve.all" />)}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 24px',
      background: active ? 'white' : 'transparent',
      color: active ? '#1e40af' : '#475569',
      border: 'none',
      borderBottom: active ? '2px solid #1e40af' : '2px solid transparent',
      marginBottom: -2,
      fontSize: 14,
      fontWeight: active ? 600 : 400,
      cursor: 'pointer',
    }}>
      {children}
    </button>
  );
}

function NoPermission({ perm }) {
  return (
    <div style={{
      padding: 24, background: '#FEF3C7', color: '#78350F',
      borderRadius: 6, border: '1px solid #F59E0B',
    }}>
      <strong>Accès refusé.</strong> Permission requise: <code>{perm}</code>
    </div>
  );
}

// =====================================================================
// TAB 1: À valider
// =====================================================================
export function TabValider() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // JFR (17-08-2026) — fériés week-end à créditer (déclencheur HUMAIN, demande Florent)
  // 18/08: crédit PERSONNE-PAR-PERSONNE avec avertissements automatiques (demande Florent)
  const [jfrPending, setJfrPending] = useState([]);
  const [jfrBusy, setJfrBusy] = useState(false);
  const [jfrOpen, setJfrOpen] = useState(null);        // date du férié dont la liste est ouverte
  const [jfrCands, setJfrCands] = useState(null);      // { ferie, candidates } du férié ouvert
  const [jfrSel, setJfrSel] = useState(new Set());     // userIds cochés

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await leavesApi.listPending();
      setPending(list);
      try { setJfrPending(await leavesApi.jfrPending()); } catch (_) { /* perm/legacy */ }
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Ouvre la liste des candidats d'un férié (pré-coche les « ok » non crédités)
  const handleJfrOpen = async (f) => {
    if (jfrOpen === f.date) { setJfrOpen(null); setJfrCands(null); return; }
    setJfrBusy(true);
    try {
      const data = await leavesApi.jfrCandidates(f.date);
      setJfrCands(data);
      setJfrSel(new Set(data.candidates.filter(c => c.preChecked).map(c => c.userId)));
      setJfrOpen(f.date);
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setJfrBusy(false);
    }
  };

  const handleJfrToggle = (userId) => {
    setJfrSel(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  // Crédit sélectif — uniquement les personnes cochées
  const handleJfrCreditSelected = async () => {
    if (!jfrCands || jfrSel.size === 0) return;
    const withWarn = jfrCands.candidates.filter(c => jfrSel.has(c.userId) && c.level !== 'ok');
    let msg = `Créditer +1 jour JFR (${jfrCands.ferie.name}, ${jfrCands.ferie.date}) à ${jfrSel.size} personne(s) sélectionnée(s) ?`;
    if (withWarn.length) msg += `\n\n⚠️ Dont ${withWarn.length} avec avertissement :\n` + withWarn.map(c => `• ${c.name} — ${c.warnings.join(' ; ')}`).join('\n');
    msg += '\n\nAction tracée dans l\'audit.';
    if (!confirm(msg)) return;
    setJfrBusy(true);
    try {
      const r = await leavesApi.jfrCredit(jfrCands.ferie.date, [...jfrSel]);
      alert(`✅ ${r.credited} collaborateur(s) crédité(s) de +1 jour JFR pour ${r.ferie}.` + (r.skipped?.length ? `\n(${r.skipped.length} ignoré(s) — déjà crédité(s))` : ''));
      setJfrOpen(null); setJfrCands(null);
      await refresh();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setJfrBusy(false);
    }
  };

  const handleApprove = async (id) => {
    if (!confirm('Approuver cette demande ?')) return;
    setBusyId(id);
    try {
      const r = await leavesApi.approve(id);
      alert(`Demande approuvée: ${r.workdaysCount} jour(s) ouvré(s). ${r.attendanceCreated} entrée(s) attendance créée(s).`);
      await refresh();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id) => {
    if (!rejectReason.trim() || rejectReason.length < 3) {
      alert('Motif de refus obligatoire (3 caractères minimum).');
      return;
    }
    setBusyId(id);
    try {
      await leavesApi.reject(id, rejectReason);
      setRejectingId(null);
      setRejectReason('');
      await refresh();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div>Chargement…</div>;

  // Bannière JFR — fériés week-end avec des personnes encore à créditer (personne-par-personne, Florent 18/08)
  const jfrToCredit = jfrPending.filter(f => (f.pendingCount ?? (f.credited ? 0 : 1)) > 0);
  const jfrLevelBadge = (c) => {
    if (c.alreadyCredited) return <span style={{ fontSize: 11, color: '#64748B' }}>déjà crédité le {String(c.creditedAt).slice(0, 10)}</span>;
    if (c.level === 'block') return <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626' }}>⛔ pas de droit</span>;
    if (c.level === 'warn') return <span style={{ fontSize: 11, fontWeight: 700, color: '#D97706' }}>⚠️ à vérifier</span>;
    return <span style={{ fontSize: 11, fontWeight: 700, color: '#16A34A' }}>✓ éligible</span>;
  };
  const jfrBanner = jfrToCredit.length > 0 && (
    <div style={{ marginBottom: 14, padding: '12px 16px', background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E', marginBottom: 6 }}>
        🗓️ Férié(s) tombé(s) un week-end — jour de remplacement (JFR) à créditer :
      </div>
      {jfrToCredit.map(f => (
        <div key={f.date} style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13 }}>
              <strong>{f.name}</strong> — {f.date} ({f.dow})
              <span style={{ color: '#A16207' }}> · {f.pendingCount} en attente{f.creditedCount ? `, ${f.creditedCount} déjà crédité(s)` : ''}</span>
            </span>
            <button onClick={() => handleJfrOpen(f)} disabled={jfrBusy}
              style={{ padding: '4px 12px', fontSize: 12, fontWeight: 600, background: jfrOpen === f.date ? '#64748B' : '#2563EB', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer' }}>
              {jfrOpen === f.date ? 'Fermer la liste' : 'Choisir les personnes…'}
            </button>
          </div>
          {jfrOpen === f.date && jfrCands && (
            <div style={{ marginTop: 8, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, padding: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748B' }}>
                    <th style={{ padding: '3px 6px' }}></th>
                    <th style={{ padding: '3px 6px' }}>Collaborateur</th>
                    <th style={{ padding: '3px 6px' }}>Contrat</th>
                    <th style={{ padding: '3px 6px' }}>Statut</th>
                    <th style={{ padding: '3px 6px' }}>Avertissements</th>
                  </tr>
                </thead>
                <tbody>
                  {jfrCands.candidates.map(c => (
                    <tr key={c.userId} style={{ borderTop: '1px solid #F1F5F9', background: c.alreadyCredited ? '#F8FAFC' : (c.level === 'block' ? '#FEF2F2' : c.level === 'warn' ? '#FFFBEB' : '#fff') }}>
                      <td style={{ padding: '3px 6px' }}>
                        <input type="checkbox" checked={jfrSel.has(c.userId)} disabled={c.alreadyCredited || jfrBusy}
                          onChange={() => handleJfrToggle(c.userId)} />
                      </td>
                      <td style={{ padding: '3px 6px', fontWeight: 600, color: c.alreadyCredited ? '#94A3B8' : '#0F172A' }}>{c.name}{c.masked ? ' 🙈' : ''}</td>
                      <td style={{ padding: '3px 6px' }}>{c.contractType}</td>
                      <td style={{ padding: '3px 6px' }}>{jfrLevelBadge(c)}</td>
                      <td style={{ padding: '3px 6px', color: c.level === 'block' ? '#DC2626' : '#B45309' }}>{c.warnings.join(' ; ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <button onClick={handleJfrCreditSelected} disabled={jfrBusy || jfrSel.size === 0}
                  style={{ padding: '5px 14px', fontSize: 12, fontWeight: 700, background: jfrSel.size ? '#16A34A' : '#94A3B8', color: '#fff', border: 0, borderRadius: 5, cursor: jfrSel.size ? 'pointer' : 'default' }}>
                  ✓ Créditer +1 JFR aux {jfrSel.size} sélectionné(s)
                </button>
                <span style={{ fontSize: 11, color: '#64748B' }}>
                  Pré-cochés : éligibles sans réserve. ⚠️/⛔ à cocher manuellement si vous décidez d'accorder malgré tout.
                </span>
              </div>
            </div>
          )}
        </div>
      ))}
      <div style={{ fontSize: 11, color: '#A16207', marginTop: 6 }}>
        Crédit personne-par-personne, tracé dans l'audit (y compris les avertissements acceptés).
      </div>
    </div>
  );

  if (pending.length === 0) {
    return (
      <div>
        {jfrBanner}
        <div style={{ padding: 32, background: '#F8FAFC', borderRadius: 6, textAlign: 'center', color: '#64748B' }}>
          Aucune demande en attente.
        </div>
      </div>
    );
  }

  return (
    <div>
    {jfrBanner}
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: '#F8FAFC' }}>
          <tr>
            <th style={thStyle}>Opérateur</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Du</th>
            <th style={thStyle}>Au</th>
            <th style={thStyle}>Motif</th>
            <th style={thStyle}>Soumis</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {pending.map(l => (
            <tr key={l.id} style={{ borderTop: '1px solid #E2E8F0' }}>
              <td style={tdStyle}>
                <strong>{l.user?.name || l.userId}</strong>
                <div style={{ fontSize: 11, color: '#64748B' }}>{l.user?.username}</div>
              </td>
              <td style={tdStyle}>
                <strong>{l.type}</strong>
                {/* Day 11++ (29-05-2026) — show partial duration for REC/AA heures requests */}
                {l.partialDurationMin && (
                  <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, marginTop: 2 }}>
                    ⏱ {Math.floor(l.partialDurationMin / 60)}h{String(l.partialDurationMin % 60).padStart(2, '0')} (heures)
                  </div>
                )}
              </td>
              <td style={tdStyle}>{formatDateFR(l.startDate)}</td>
              <td style={tdStyle}>{l.partialDurationMin ? <span style={{ color: '#94a3b8' }}>— même jour</span> : formatDateFR(l.endDate)}</td>
              <td style={tdStyle}>{l.reason || '—'}</td>
              <td style={tdStyle}>{formatDateFR(l.submittedAt?.slice(0, 10))}</td>
              <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                {rejectingId === l.id ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="text" placeholder="Motif refus" value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      style={{ ...inputStyle, width: 180, padding: '4px 8px' }} />
                    <button onClick={() => handleReject(l.id)} disabled={busyId === l.id}
                      style={{ padding: '4px 10px', background: '#DC2626', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                      Confirmer
                    </button>
                    <button onClick={() => { setRejectingId(null); setRejectReason(''); }}
                      style={{ padding: '4px 10px', background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                      Annuler
                    </button>
                  </div>
                ) : (
                  <>
                    <button onClick={() => handleApprove(l.id)} disabled={busyId === l.id}
                      style={{ padding: '4px 10px', marginRight: 4, background: '#15803d', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                      ✓ Approuver
                    </button>
                    <button onClick={() => setRejectingId(l.id)} disabled={busyId === l.id}
                      style={{ padding: '4px 10px', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                      ✗ Refuser
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

// =====================================================================
// TAB 2: Encodage direct
// =====================================================================
export function TabDirect() {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [type, setType] = useState('VA');
  const [startDate, setStartDate] = useState(todayYmd());
  const [endDate, setEndDate] = useState(todayYmd());
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // Day 11 — REC variante A: toggle jours / heures + solde OT
  const [recMode, setRecMode] = useState('jours');   // 'jours' | 'heures'
  const [recHours, setRecHours] = useState('');       // hh:mm pour mode heures
  const [otBalances, setOtBalances] = useState([]);   // soldes OT par user

  useEffect(() => {
    // Encodage direct : ACTIFS seulement. Le serveur refuse un compte
    // désactivé (leave.service.js, USER_INACTIVE) — le proposer serait
    // offrir un choix qui ne peut pas aboutir.
    usersApi.list({ active: 'true' })
      .then(d => setUsers(Array.isArray(d) ? d : (d.items || d.users || [])))
      .catch(() => {});
    overtimeApi.adminBalances(false).then(d => setOtBalances(Array.isArray(d) ? d : (d.items || []))).catch(() => {});
  }, []);

  // Solde OT du user sélectionné (heures, signé)
  // Day 11++ (29-05-2026) — fix: backend renvoie `balanceHours` (pas balance/hours/totalHours).
  // L'ancien fallback ne matchait jamais → toujours 0h00 affiché, bloquant l'encodage REC heures.
  const otForUser = (() => {
    const row = otBalances.find(b => b.userId === userId || b.id === userId);
    return row ? (row.balanceHours ?? row.balance ?? row.hours ?? row.totalHours ?? 0) : null;
  })();
  function parseHHMMtoMin(s) {
    if (!s) return NaN;
    const t = String(s).trim();
    const m = t.match(/^(\d+):([0-5]?\d)$/);
    if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    const dec = parseFloat(t.replace(',', '.'));
    return isNaN(dec) ? NaN : Math.round(dec * 60);
  }

  const handleSubmit = async () => {
    setFeedback(null);
    if (!userId) {
      setFeedback({ type: 'error', message: 'Sélectionnez un opérateur.' });
      return;
    }
    const supportsHours = type === 'REC' || type === 'AA';
    const isHoursMode = supportsHours && recMode === 'heures';
    if (!isHoursMode && startDate > endDate) {
      setFeedback({ type: 'error', message: 'Date fin doit être ≥ date début.' });
      return;
    }
    let partialDurationMin = null;
    if (isHoursMode) {
      partialDurationMin = parseHHMMtoMin(recHours);
      if (isNaN(partialDurationMin) || partialDurationMin < 15 || partialDurationMin > 456) {
        setFeedback({ type: 'error', message: `Durée ${type} invalide. Format hh:mm entre 0:15 et 7:36 (ex: 1:00).` });
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = isHoursMode
        ? { userId, type, startDate, endDate: startDate, reason: reason.trim() || null, partialDurationMin }
        : { userId, type, startDate, endDate, reason: reason.trim() || null };
      const r = await leavesApi.adminDirect(payload);
      const u = users.find(x => x.id === userId);
      setFeedback({
        type: 'success',
        message: isHoursMode
          ? `${type} en heures créé pour ${u?.name || userId}: ${recHours} le ${formatDateFR(startDate)} — présence + TS générés${type === 'REC' ? ', solde OT débité' : ''}.`
          : `Congé créé pour ${u?.name || userId}: ${type} du ${formatDateFR(startDate)} au ${formatDateFR(endDate)} — ${r.workdaysCount} jour(s) ouvré(s), ${r.attendanceCreated} attendance créée(s).`,
      });
      setReason(''); setRecHours('');
    } catch (err) {
      const apiErr = err.response?.data;
      let msg = apiErr?.message || err.message || 'Erreur inconnue';
      if (apiErr?.error === 'OVERLAP') msg = 'âš  ' + msg;
      else if (apiErr?.error === 'PERIOD_LOCKED') msg = 'ðŸ”’ ' + msg;
      setFeedback({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 8, padding: 20, maxWidth: 720 }}>
      <div style={{ marginBottom: 12, padding: 10, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, fontSize: 12, color: '#1E40AF' }}>
        ℹ️ L'encodage direct crée immédiatement un congé <strong>APPROUVÉ</strong> sans passer par la demande opérateur. Utile pour cas urgents ou rétroactifs.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Field label="OPÉRATEUR">
          <select value={userId} onChange={e => setUserId(e.target.value)} style={inputStyle} disabled={submitting}>
            <option value="">— Sélectionner —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.username})</option>)}
          </select>
        </Field>
        <Field label="TYPE">
          <select value={type} onChange={e => setType(e.target.value)} style={inputStyle} disabled={submitting}>
            {Object.entries(LEAVE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
          </select>
        </Field>
        {/* Day 11 — REC + AA: toggle jours/heures + (REC) solde OT */}
        {(type === 'REC' || type === 'AA') && (
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 0' }}>
            <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden' }}>
              <div onClick={() => !submitting && setRecMode('jours')} style={{ padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: recMode === 'jours' ? '#4f46e5' : '#fff', color: recMode === 'jours' ? '#fff' : '#64748b' }}>📅 Jours complets</div>
              <div onClick={() => !submitting && setRecMode('heures')} style={{ padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: recMode === 'heures' ? '#4f46e5' : '#fff', color: recMode === 'heures' ? '#fff' : '#64748b' }}>⏱️ Heures</div>
            </div>
            {type === 'REC' && userId && otForUser !== null && (
              <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, background: otForUser > 0 ? '#ecfdf5' : '#fef2f2', color: otForUser > 0 ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                Solde heures sup. : {Math.floor(Math.abs(otForUser))}h{String(Math.round((Math.abs(otForUser) % 1) * 60)).padStart(2, '0')}{otForUser < 0 ? ' (négatif)' : ''}
              </span>
            )}
          </div>
        )}
        <Field label="DATE DÉBUT">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} disabled={submitting} />
        </Field>
        {(type === 'REC' || type === 'AA') && recMode === 'heures' ? (
          <Field label="DURÉE (hh:mm)">
            <input type="text" value={recHours} onChange={e => setRecHours(e.target.value)} placeholder={type === 'AA' ? 'Ex: 1:00' : 'Ex: 2:24'} style={inputStyle} disabled={submitting} />
          </Field>
        ) : (
          <Field label="DATE FIN">
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inputStyle} disabled={submitting} />
          </Field>
        )}
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="MOTIF (optionnel)">
            <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Ex: opérateur en maladie, encodé par admin" style={inputStyle} maxLength={500} disabled={submitting} />
          </Field>
        </div>
      </div>

      {feedback && (
        <div style={{
          padding: 12, marginBottom: 12, borderRadius: 6, fontSize: 13,
          background: feedback.type === 'success' ? '#DCFCE7' : '#FEE2E2',
          color: feedback.type === 'success' ? '#166534' : '#991B1B',
          border: '1px solid ' + (feedback.type === 'success' ? '#86EFAC' : '#FCA5A5'),
        }}>{feedback.message}</div>
      )}

      <button onClick={handleSubmit} disabled={submitting} style={{
        padding: '10px 24px', background: submitting ? '#94a3b8' : '#15803d',
        color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500,
        cursor: submitting ? 'wait' : 'pointer',
      }}>
        {submitting ? 'Envoi…' : 'Créer le congé (approuvé)'}
      </button>
    </div>
  );
}

// =====================================================================
// TAB 3: Cotes annuelles
// =====================================================================
export function TabCotes() {
  const currentYear = new Date().getFullYear();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [year, setYear] = useState(currentYear);
  const [balance, setBalance] = useState(null);
  const [quotas, setQuotas] = useState({ VA: 0, VAN: 0, VAEN1: 0, AA: 0, REC: 0, MAT: 0, PAT: 0 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    // Quotas : comptes INACTIFS compris. On corrige encore le solde de
    // quelqu'un parti (solde de tout compte, année précédente). Ils sont
    // marqués « (inactif) », pas cachés.
    usersApi.list()
      .then(d => setUsers(Array.isArray(d) ? d : (d.items || d.users || [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!userId || !year) {
      setBalance(null);
      setQuotas({ VA: 0, VAN: 0, VAEN1: 0, AA: 0, REC: 0, MAT: 0, PAT: 0 });
      return;
    }
    setLoading(true);
    balanceApi.getForUser(userId, year)
      .then(d => {
        setBalance(d);
        const q = { VA: 0, VAN: 0, VAEN1: 0, AA: 0, REC: 0, MAT: 0, PAT: 0 };
        for (const item of d.quotas) {
          if (q.hasOwnProperty(item.type)) q[item.type] = item.totalAllocated;
        }
        setQuotas(q);
      })
      .catch(err => alert('Erreur chargement: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoading(false));
  }, [userId, year]);

  const handleSave = async () => {
    setFeedback(null);
    setSaving(true);
    try {
      await balanceApi.setForUser(userId, year, quotas);
      const updated = await balanceApi.getForUser(userId, year);
      setBalance(updated);
      const u = users.find(x => x.id === userId);
      setFeedback({ type: 'success', message: `Quotas mis à jour pour ${u?.name || userId} (${year}).` });
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 8, padding: 20, maxWidth: 900 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 20 }}>
        <Field label="OPÉRATEUR">
          <select value={userId} onChange={e => setUserId(e.target.value)} style={inputStyle}>
            <option value="">— Sélectionner —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.username}){marqueurInactif(u)}</option>)}
          </select>
        </Field>
        <Field label="ANNÉE">
          <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value, 10) || currentYear)}
            min={2020} max={2100} style={inputStyle} />
        </Field>
      </div>

      {userId && (
        <>
          {loading ? (
            <div style={{ color: '#64748B' }}>Chargement…</div>
          ) : (
            <>
              <h3 style={{ fontSize: 14, marginBottom: 12, color: '#1e293b' }}>
                Quotas de congés annuels (jours)
              </h3>
              <p style={{ fontSize: 12, color: '#64748B', marginBottom: 16 }}>
                Définissez le total alloué par type. Les jours utilisés (validés) sont affichés en gris.
                <br />
                <em style={{ fontSize: 11 }}>AA et REC ne sont pas des droits annuels — leur historique d'utilisation reste disponible dans "Vue d'ensemble" et "Soldes par utilisateur".</em>
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
                {['VA', 'VAN', 'VAEN1', 'MAT', 'PAT'].map(type => {
                  const stored = balance?.quotas.find(q => q.type === type);
                  return (
                    <Field key={type} label={`${type} — ${LEAVE_TYPE_LABELS[type]}`}>
                      <input
                        type="number" step="0.5" min={0} max={999}
                        value={quotas[type]}
                        onChange={e => setQuotas({ ...quotas, [type]: parseFloat(e.target.value) || 0 })}
                        style={inputStyle}
                      />
                      {stored && (stored.used > 0 || (stored.pending || 0) > 0) && (
                        <div style={{ fontSize: 11, color: '#64748B', marginTop: 4, lineHeight: 1.5 }}>
                          {stored.used > 0 && (
                            <div>
                              utilisés: <strong>{stored.used}</strong> j ·
                              approuvés: <strong>{stored.used}</strong> j
                              <span style={{ marginLeft: 6, color: '#94a3b8' }}>(restant: {stored.remaining})</span>
                            </div>
                          )}
                          {(stored.pending || 0) > 0 && (
                            <div style={{ color: '#92400e', fontWeight: 600 }}>
                              en attente: {stored.pending} j
                            </div>
                          )}
                        </div>
                      )}
                    </Field>
                  );
                })}
              </div>

              {feedback && (
                <div style={{
                  padding: 12, marginBottom: 12, borderRadius: 6, fontSize: 13,
                  background: feedback.type === 'success' ? '#DCFCE7' : '#FEE2E2',
                  color: feedback.type === 'success' ? '#166534' : '#991B1B',
                  border: '1px solid ' + (feedback.type === 'success' ? '#86EFAC' : '#FCA5A5'),
                }}>{feedback.message}</div>
              )}

              <button onClick={handleSave} disabled={saving} style={{
                padding: '10px 24px', background: saving ? '#94a3b8' : '#15803d',
                color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500,
                cursor: saving ? 'wait' : 'pointer',
              }}>
                {saving ? 'Enregistrement…' : 'Enregistrer les cotes'}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 500, color: '#64748B',
        marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>{label}</label>
      {children}
    </div>
  );
}

// =====================================================================
// TAB 4: Historique (cancel APPROVED Leave) — #5c Day 8
// =====================================================================

export function TabHistorique() {
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await leavesApi.listAll({ status: 'APPROVED' });
      // Sort by startDate DESC (cele mai recente primele)
      list.sort((a, b) => b.startDate.localeCompare(a.startDate));
      setApproved(list);
    } catch (err) {
      alert('Erreur chargement: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleCancel = async (leave) => {
    // Calc workdays approximative (doar pentru mesaj UI; backend re-calcula real)
    const start = new Date(leave.startDate);
    const end = new Date(leave.endDate);
    const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const balanceType = leave.type === 'VA-0.5' ? 'VA' : leave.type;

    const msg = 'Confirmer l\'annulation du conge ' + leave.type + ' de ' + (leave.user?.name || leave.userId) +
      ' (' + formatDateFR(leave.startDate) + ' - ' + formatDateFR(leave.endDate) + ', ~' + diffDays + ' jour(s)) ?\n\n' +
      'Cette action va:\n' +
      '- supprimer les entrées de timesheet associées\n' +
      '- restituer le solde ' + balanceType + ' (' + (leave.type === 'VA-0.5' ? 'demi-journée' : diffDays + ' jour(s)') + ')\n' +
      '- marquer le congé comme CANCELLED\n\n' +
      'Cette opération est IRRÉVERSIBLE.';

    if (!confirm(msg)) return;

    setBusyId(leave.id);
    try {
      const r = await leavesApi.cancelApproved(leave.id);
      alert('Congé annulé:\n' +
        '- ' + r.attendancesDeleted + ' présence(s) supprimée(s)\n' +
        '- ' + r.tsEntriesDeleted + ' entrée(s) timesheet supprimée(s)\n' +
        '- ' + r.balanceDaysReverted + ' j ' + r.balanceType + ' restitué(s)');
      await refresh();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.message || err.message));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div>Chargement…</div>;

  // Filter local
  let filtered = approved;
  if (filterUser) {
    const q = filterUser.toLowerCase();
    filtered = filtered.filter(l =>
      (l.user?.name || '').toLowerCase().includes(q) ||
      (l.user?.username || '').toLowerCase().includes(q)
    );
  }
  if (filterType) {
    filtered = filtered.filter(l => l.type === filterType);
  }

  return (
    <div>
      {/* Filtres */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Filtrer par utilisateur…"
          value={filterUser}
          onChange={e => setFilterUser(e.target.value)}
          style={{ ...inputStyle, width: 240 }}
        />
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          style={{ ...inputStyle, width: 200 }}
        >
          <option value="">Tous les types</option>
          {Object.entries(LEAVE_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <span style={{ fontSize: 13, color: '#64748B' }}>
          {filtered.length} / {approved.length} congé(s) APPROUVÉ(s)
        </span>
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 32, background: '#F8FAFC', borderRadius: 6, textAlign: 'center', color: '#64748B' }}>
          Aucun congé approuvé {filterUser || filterType ? '(filtres appliqués)' : ''}.
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#F8FAFC' }}>
              <tr>
                <th style={thStyle}>Utilisateur</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Du</th>
                <th style={thStyle}>Au</th>
                <th style={thStyle}>Décidé par</th>
                <th style={thStyle}>Décidé le</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(l => (
                <tr key={l.id} style={{ borderTop: '1px solid #E2E8F0' }}>
                  <td style={tdStyle}>
                    <strong>{l.user?.name || l.userId}</strong>
                    <div style={{ fontSize: 11, color: '#64748B' }}>{l.user?.username}</div>
                  </td>
                  <td style={tdStyle}>
                    <strong>{l.type}</strong>
                    <div style={{ fontSize: 11, color: '#64748B' }}>{LEAVE_TYPE_LABELS[l.type] || ''}</div>
                  </td>
                  <td style={tdStyle}>{formatDateFR(l.startDate)}</td>
                  <td style={tdStyle}>{formatDateFR(l.endDate)}</td>
                  <td style={tdStyle}>{l.decidedByUser?.name || l.decidedByUserId || '—'}</td>
                  <td style={tdStyle}>{formatDateFR(l.decidedAt?.slice(0, 10))}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => handleCancel(l)}
                      disabled={busyId === l.id}
                      style={{
                        padding: '4px 12px',
                        background: '#FEE2E2',
                        color: '#991B1B',
                        border: '1px solid #FCA5A5',
                        borderRadius: 4,
                        fontSize: 12,
                        cursor: busyId === l.id ? 'wait' : 'pointer',
                        opacity: busyId === l.id ? 0.6 : 1,
                      }}
                    >
                      ✗ Annuler
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
// force-rebuild 2026-05-22T19-29-34
